import React, { useEffect, useState } from "react";
import { X, Ticket, Check, Share2, Copy, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

const SEEN_KEY = "nk-club-popup-v1";

export default function ClubPopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", consent: true, referred_by: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get("ref") || "").trim().toUpperCase();
    if (ref) {
      setForm((f) => ({ ...f, referred_by: ref }));
      api
        .get(`/referral/validate?code=${encodeURIComponent(ref)}`)
        .then((r) => {
          if (r.data.valid) setReferrerInfo(r.data);
        })
        .catch(() => {});
      if (!localStorage.getItem(SEEN_KEY)) {
        setOpen(true);
        return;
      }
    }
    if (localStorage.getItem(SEEN_KEY)) return;
    const t = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(SEEN_KEY, "dismissed");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const { data } = await api.post("/subscribers", form);
      setVoucher({ code: data.voucher_code, offer: data.voucher_offer });
      localStorage.setItem(SEEN_KEY, "signed-up");
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err.response?.data?.detail || err.message || "Something went wrong.");
    }
  };

  const onChange = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const shareLink = voucher
    ? `${window.location.origin}/?ref=${encodeURIComponent(voucher.code)}`
    : "";

  const shareMessage = voucher
    ? `I just joined the Naami Kitchens Club — twenty brands under one roof in Edinburgh. Use my code to join too and get invited to our official launch + exclusive perks: ${shareLink}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      /* noop */
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 fade-up"
      onClick={dismiss}
      role="dialog"
      aria-label="Join the Naami Club"
      data-testid="club-popup"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[520px] bg-[var(--surface-elevated)] border border-[var(--border)] rounded-2xl overflow-hidden grain max-h-[92vh] overflow-y-auto"
        style={{ animation: "fadeUp 0.45s ease forwards" }}
      >
        <div className="absolute -top-24 -right-24 w-[300px] h-[300px] rounded-full bg-[var(--gold)] opacity-20 blur-3xl pointer-events-none" aria-hidden />

        <button
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-[var(--text)] transition-colors"
          data-testid="club-popup-close"
        >
          <X size={16} />
        </button>

        <div className="relative z-[1] p-8 md:p-10">
          {status === "sent" ? (
            <div className="text-center" data-testid="club-popup-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-[var(--gold)] flex items-center justify-center mb-5">
                <Check size={24} className="text-[var(--bg)]" />
              </div>
              <h2 className="font-display text-[40px] leading-none">You're in.</h2>
              <p className="text-muted text-[14px] mt-3 max-w-xs mx-auto">
                Welcome to the Naami Club. Save your member code — we'll be in
                touch with launch details and exclusive perks soon.
              </p>

              <div className="mt-5 mx-auto max-w-[320px] rounded-xl border-2 border-dashed border-[var(--gold)] bg-[var(--gold)]/10 p-5" data-testid="popup-voucher">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                  Your Naami Club Code
                </p>
                <p className="font-display text-[36px] tracking-wider text-gold mt-2 select-all" data-testid="popup-voucher-code">
                  {voucher?.code || "—"}
                </p>
                <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-muted mt-2">
                  Keep this safe · We'll reference it when we get in touch
                </p>
              </div>

              <div className="mt-7 pt-6 border-t border-[var(--border)]">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold flex items-center gap-2 justify-center">
                  <Share2 size={12} /> Refer a Friend · Exclusive Perks for Both
                </p>
                <p className="text-muted text-[12.5px] mt-2 max-w-xs mx-auto leading-relaxed">
                  Share your link — friends who join the Club with your code
                  get the same launch invite, and we'll send you a bonus perk
                  when they sign up.
                </p>

                <div className="flex gap-2 mt-4">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="plat-btn"
                    style={{ background: "#25D366", color: "#022", padding: "10px 14px", fontSize: "11px", flex: 1 }}
                    data-testid="popup-share-whatsapp"
                  >
                    <MessageCircle size={12} style={{ marginRight: 4 }} />
                    WhatsApp
                  </a>
                  <button
                    onClick={copyLink}
                    className="plat-btn"
                    style={{ background: "var(--surface-elevated)", border: "1px solid var(--border)", color: "var(--text)", padding: "10px 14px", fontSize: "11px", flex: 1 }}
                    data-testid="popup-share-copy"
                  >
                    <Copy size={12} style={{ marginRight: 4 }} />
                    {copied ? "Copied!" : "Copy link"}
                  </button>
                </div>
              </div>

              <button
                onClick={dismiss}
                className="btn-ghost mt-6 mx-auto"
                style={{ padding: "10px 18px", fontSize: "11px" }}
                data-testid="club-popup-close-after"
              >
                Continue exploring
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <Ticket size={13} className="text-gold" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                  {referrerInfo ? `${referrerInfo.referrer_first_name} invited you to the Naami Club` : "Join the Naami Club"}
                </span>
              </div>
              <h2 className="font-display text-[42px] md:text-[52px] leading-[0.95]">
                Join the <span className="text-gold">Naami</span><br />
                Club.
              </h2>
              <p className="text-muted text-[14px] mt-3 leading-relaxed max-w-[400px]">
                {referrerInfo
                  ? `${referrerInfo.referrer_first_name} thinks you'll love Naami Kitchens. Join the Club to get an invite to our official launch, exclusive discounts, and first dibs on new brands.`
                  : "Be invited to our official launch, unlock exclusive discounts, and get first dibs on new brands as they open under our roof."}
              </p>

              <form onSubmit={submit} className="mt-6 flex flex-col gap-3" data-testid="club-popup-form">
                <div className="grid grid-cols-2 gap-3">
                  <input required value={form.name} onChange={onChange("name")} placeholder="Your name *" className="input-nk" data-testid="popup-name" />
                  <input required value={form.phone} onChange={onChange("phone")} placeholder="Phone *" className="input-nk" data-testid="popup-phone" />
                </div>
                <input type="email" value={form.email} onChange={onChange("email")} placeholder="Email (optional)" className="input-nk" data-testid="popup-email" />

                <input
                  value={form.referred_by}
                  onChange={(e) => setForm({ ...form, referred_by: e.target.value.toUpperCase() })}
                  placeholder="Referral code (optional)"
                  className="input-nk"
                  data-testid="popup-referred-by"
                />

                <label className="flex items-start gap-2 text-[11.5px] text-muted leading-relaxed cursor-pointer">
                  <input type="checkbox" checked={form.consent} onChange={onChange("consent")} className="mt-0.5" data-testid="popup-consent" />
                  <span>I'm happy to receive launch invites, exclusive deals and updates from Naami Kitchens.</span>
                </label>

                {error && <p className="text-[var(--error)] text-[12px]">{error}</p>}

                <button
                  type="submit"
                  disabled={status === "sending" || !form.consent}
                  className="btn-primary justify-center mt-1"
                  data-testid="popup-submit"
                >
                  {status === "sending" ? "Joining…" : "Join the Club →"}
                </button>

                <button
                  type="button"
                  onClick={dismiss}
                  className="text-muted hover:text-[var(--text)] font-mono text-[10px] uppercase tracking-[0.18em] transition-colors mt-1"
                  data-testid="popup-dismiss"
                >
                  No thanks, I'll skip
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
